import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../../api/axios";
import styles from "./PlayerUpdateForm.module.css";
import {
  LuUser,
  LuUsers,
  LuUpload,
  LuPhone,
  LuTrophy,
  LuGraduationCap,
  LuMail,
  LuMapPin,
  LuSchool,
  LuBuilding,
  LuCalendar,
  LuHash,
  LuFilm,
  LuCircleCheck,
} from "react-icons/lu";
import { InputField, SelectField } from "../FormFields/FormFields.js";

const getRenderableImageSrc = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  const trimmedValue = value.trim();

  if (trimmedValue.startsWith("data:image/") || trimmedValue.startsWith("blob:")) {
    return trimmedValue;
  }

  if (/^https?:\/\/.+\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(trimmedValue)) {
    return trimmedValue;
  }

  return "";
};

const PlayerUpdateForm = () => {
  const { token } = useParams();

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  // Player data
  const [player, setPlayer] = useState(null);
  const [editablePlayer, setEditablePlayer] = useState(null);

  // State for sports and positions
  const [sports, setSports] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedSportId, setSelectedSportId] = useState("");

  // Default player fields structure
  const defaultEditPlayer = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    sport_id: "",
    position_id: "",
    secondary_position_id: "",
    jersey_number: "",
    highlight_link: "",
    grad_year: "",
    gpa: "",
    club_team: "",
    high_school: "",
    club_team_coach_name: "",
    club_team_coach_email: "",
    club_team_coach_phone: "",
    high_school_coach_name: "",
    high_school_coach_email: "",
    high_school_coach_phone: "",
    primary_parent_name: "",
    primary_parent_email: "",
    primary_parent_phone: "",
    secondary_parent_name: "",
    secondary_parent_email: "",
    secondary_parent_phone: "",
    profile_picture: "",
  };

  const buildEditablePlayer = (playerData) =>
    Object.fromEntries(
      Object.keys(defaultEditPlayer).map((key) => [
        key,
        playerData?.[key] ?? defaultEditPlayer[key],
      ])
    );

  // Fetch player data using token on component mount
  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/tokens/${token}`);
        const playerData = response.data;
        
        setPlayer(playerData);
        setSelectedSportId(playerData.sport_id || "");
        setEditablePlayer(buildEditablePlayer(playerData));
      } catch (err) {
        console.error("Error fetching player data:", err);
        setError(err.response?.data?.message || err.response?.data?.error || "Failed to load player information");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPlayerData();
    }
  }, [token]);

  // Fetch all sports when component mounts
  useEffect(() => {
    const fetchSports = async () => {
      try {
        const response = await api.get("/sports");
        setSports(response.data);
      } catch (error) {
        console.error("Error fetching sports:", error);
      }
    };
    fetchSports();
  }, []);

  // Fetch positions when selectedSportId changes
  useEffect(() => {
    const fetchPositions = async () => {
      if (!selectedSportId) {
        setPositions([]);
        return;
      }

      try {
        const response = await api.get(`/sports/${selectedSportId}/positions`);
        if (response && response.data) {
          setPositions(response.data);
        }
      } catch (error) {
        console.error("Error fetching positions:", error);
        setPositions([]);
      }
    };

    fetchPositions();
  }, [selectedSportId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If sport changes, update selectedSportId and reset positions
    if (name === "sport_id") {
      setSelectedSportId(value);
      setEditablePlayer((currentPlayer) => ({
        ...currentPlayer,
        sport_id: value,
        position_id: "",
        secondary_position_id: "",
      }));
    } else {
      setEditablePlayer((currentPlayer) => ({
        ...currentPlayer,
        [name]: value,
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      setIsProcessingImage(true);
      setSaveError(null);

      reader.onloadend = () => {
        setEditablePlayer((currentPlayer) => ({
          ...currentPlayer,
          profile_picture: typeof reader.result === "string" ? reader.result : "",
        }));
        setIsProcessingImage(false);
        e.target.value = "";
      };

      reader.onerror = () => {
        setIsProcessingImage(false);
        setSaveError("Failed to process the selected image. Please try another file.");
        e.target.value = "";
      };

      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (isProcessingImage) {
      setSaveError("Please wait for the image upload to finish before saving.");
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);
      
      // Use player_id from the fetched player data
      await api.put(`/playerupdate/${player.player_id}`, editablePlayer);

      const refreshedResponse = await api.get(`/tokens/${token}`);
      const refreshedPlayer = refreshedResponse.data;
      setPlayer(refreshedPlayer);
      setSelectedSportId(refreshedPlayer.sport_id || "");
      setEditablePlayer(buildEditablePlayer(refreshedPlayer));
      
      setSaveSuccess(true);
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error("Error saving player data:", err);
      setSaveError(err.response?.data?.message || "Failed to save changes. Please try again.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original player data
    setEditablePlayer(buildEditablePlayer(player));
    setSelectedSportId(player.sport_id || "");
    setSaveSuccess(false);
    setSaveError(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading your information...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h2>Unable to Load Information</h2>
        <p>{error}</p>
        <p className={styles.errorHint}>
          Please check that your link is correct and hasn't expired.
        </p>
      </div>
    );
  }

  // No player data
  if (!player || !editablePlayer) {
    return (
      <div className={styles.errorContainer}>
        <h2>No Player Information Found</h2>
        <p>Unable to retrieve your information.</p>
      </div>
    );
  }

  const profileImageSrc = getRenderableImageSrc(editablePlayer.profile_picture);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.formContent}>
        {/* Success Message */}
        {saveSuccess && (
          <div className={styles.successBanner}>
            <LuCircleCheck className={styles.successIcon} />
            <div>
              <h3>Information Updated Successfully!</h3>
              <p>Your changes have been saved. You can now close this window.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {saveError && (
          <div className={styles.errorBanner}>
            <p>{saveError}</p>
          </div>
        )}

        {/* Header */}
        <div className={styles.header}>
          <h1>Update Your Information</h1>
          <p className={styles.headerSubtext}>
            Please review and update your information below.
          </p>
        </div>

        {/* Profile Section */}
        <div className={styles.profileSection}>
          <div className={styles.profileImageContainer}>
            {profileImageSrc ? (
              <img
                src={profileImageSrc}
                alt="Profile"
                className={styles.profilePicture}
              />
            ) : (
              <div className={styles.profilePicturePlaceholder}>
                <LuUser style={{ fontSize: "50px" }} />
              </div>
            )}

            <button
              type="button"
              className={styles.uploadButton}
              onClick={() => document.getElementById("player-update-file-input").click()}
              disabled={isProcessingImage || isSaving}
              aria-label={profileImageSrc ? "Change profile picture" : "Upload profile picture"}
            >
              <LuUpload style={{ fontSize: "20px" }} />
            </button>
            <input
              id="player-update-file-input"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className={styles.hiddenFileInput}
            />
          </div>

          <div className={styles.profileHeader}>
            <h2 style={{ margin: "0" }}>
              {editablePlayer.first_name || ""} {editablePlayer.last_name || ""}
            </h2>
            <p style={{ margin: "0", color: "#4b4b4b" }}>
              {player?.position_name || ""}
            </p>
            {isProcessingImage && (
              <p className={styles.uploadStatus}>Processing image...</p>
            )}
          </div>
        </div>

        {/* Player Information Section */}
        <div className={styles.playerInformation}>
          {/* Personal Information */}
          <div className={styles.subHeader}>
            <LuUser className={styles.subHeaderIcon} />
            <h3>Personal Information</h3>
          </div>
          <hr className={styles.divider} />
          <div className={styles.informationGrid}>
            <InputField
              label="First Name"
              name="first_name"
              value={editablePlayer.first_name}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuUser}
            />
            <InputField
              label="Last Name"
              name="last_name"
              value={editablePlayer.last_name}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuUser}
            />
            <InputField
              label="Phone"
              name="phone"
              value={editablePlayer.phone}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuPhone}
            />
            <InputField
              label="Email"
              name="email"
              value={editablePlayer.email}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMail}
            />
            <InputField
              label="Address"
              name="address"
              value={editablePlayer.address}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMapPin}
            />
            <InputField
              label="City"
              name="city"
              value={editablePlayer.city}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMapPin}
            />
            <InputField
              label="State"
              name="state"
              value={editablePlayer.state}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMapPin}
            />
          </div>

          {/* Athletic Information */}
          <div className={styles.subHeader}>
            <LuTrophy className={styles.subHeaderIcon} />
            <h3>Athletic Information</h3>
          </div>
          <hr className={styles.divider} />
          <div className={styles.informationGrid}>
            <SelectField
              label="Sport"
              name="sport_id"
              optionId="sport_id"
              optionName="sport_name"
              value={editablePlayer.sport_id}
              onChange={handleChange}
              options={sports}
              isEditMode={true}
              display={player?.sport_name}
              Icon={LuTrophy}
            />
            <InputField
              label="Jersey Number"
              name="jersey_number"
              value={editablePlayer.jersey_number}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuHash}
            />
            <SelectField
              label="Position"
              name="position_id"
              optionId="position_id"
              optionName="position_name"
              value={editablePlayer.position_id}
              onChange={handleChange}
              options={positions}
              isEditMode={true}
              display={player?.position_name}
              Icon={LuTrophy}
            />
            <SelectField
              label="Secondary Position"
              name="secondary_position_id"
              optionId="position_id"
              optionName="position_name"
              value={editablePlayer.secondary_position_id}
              onChange={handleChange}
              options={positions}
              isEditMode={true}
              display={player?.secondary_position_name}
              Icon={LuTrophy}
            />
            <InputField
              label="Highlight Video"
              name="highlight_link"
              value={editablePlayer.highlight_link}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuFilm}
            />
          </div>

          {/* Academic Information */}
          <div className={styles.subHeader}>
            <LuGraduationCap className={styles.subHeaderIcon} />
            <h3>Academic Information</h3>
          </div>
          <hr className={styles.divider} />
          <div className={styles.informationGrid}>
            <InputField
              label="Graduation Year"
              name="grad_year"
              value={editablePlayer.grad_year}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuCalendar}
            />
            <InputField
              label="GPA"
              name="gpa"
              value={editablePlayer.gpa}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuGraduationCap}
            />
            <InputField
              label="Club Team"
              name="club_team"
              value={editablePlayer.club_team}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuBuilding}
            />
            <InputField
              label="High School"
              name="high_school"
              value={editablePlayer.high_school}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuSchool}
            />
          </div>

          {/* Additional Contacts */}
          <div className={styles.subHeader}>
            <LuUsers className={styles.subHeaderIcon} />
            <h3>Additional Contacts</h3>
          </div>
          <hr className={styles.divider} />
          <h4 className={styles.subLabels}>Club Coach</h4>
          <div className={styles.informationGrid}>
            <InputField
              label="Name"
              name="club_team_coach_name"
              value={editablePlayer.club_team_coach_name}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuUsers}
            />
            <InputField
              label="Email"
              name="club_team_coach_email"
              value={editablePlayer.club_team_coach_email}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMail}
            />
            <InputField
              label="Phone"
              name="club_team_coach_phone"
              value={editablePlayer.club_team_coach_phone}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuPhone}
            />
          </div>
          <h4 className={styles.subLabels}>High School Coach</h4>
          <div className={styles.informationGrid}>
            <InputField
              label="Name"
              name="high_school_coach_name"
              value={editablePlayer.high_school_coach_name}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuUsers}
            />
            <InputField
              label="Email"
              name="high_school_coach_email"
              value={editablePlayer.high_school_coach_email}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMail}
            />
            <InputField
              label="Phone"
              name="high_school_coach_phone"
              value={editablePlayer.high_school_coach_phone}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuPhone}
            />
          </div>
          <h4 className={styles.subLabels}>Primary Parent</h4>
          <div className={styles.informationGrid}>
            <InputField
              label="Name"
              name="primary_parent_name"
              value={editablePlayer.primary_parent_name}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuUser}
            />
            <InputField
              label="Phone"
              name="primary_parent_phone"
              value={editablePlayer.primary_parent_phone}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuPhone}
            />
            <InputField
              label="Email"
              name="primary_parent_email"
              value={editablePlayer.primary_parent_email}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMail}
            />
          </div>
          <h4 className={styles.subLabels}>Secondary Parent</h4>
          <div className={styles.informationGrid}>
            <InputField
              label="Name"
              name="secondary_parent_name"
              value={editablePlayer.secondary_parent_name}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuUser}
            />
            <InputField
              label="Phone"
              name="secondary_parent_phone"
              value={editablePlayer.secondary_parent_phone}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuPhone}
            />
            <InputField
              label="Email"
              name="secondary_parent_email"
              value={editablePlayer.secondary_parent_email}
              onChange={handleChange}
              isEditMode={true}
              Icon={LuMail}
            />
          </div>
        </div>

        {/* Button Group */}
        <div className={styles.buttonGroup}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={isSaving || saveSuccess || isProcessingImage}
          >
            {isProcessingImage
              ? "Processing Image..."
              : isSaving
                ? "Saving..."
                : saveSuccess
                  ? "Saved!"
                  : "Save Changes"}
          </button>
          <button
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSaving || saveSuccess || isProcessingImage}
          >
            Clear Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerUpdateForm;

